import React, { useState, useEffect } from 'react';
import { Calculator, Mail, Eye, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const EmailOpenRateCalculator = () => {
  const [emailsSent, setEmailsSent] = useState(10000);
  const [emailsOpened, setEmailsOpened] = useState(2000);
  const [openRate, setOpenRate] = useState(0);

  useEffect(() => {
    calculateOpenRate();
  }, [emailsSent, emailsOpened]);

  const calculateOpenRate = () => {
    const rate = (emailsOpened / emailsSent) * 100;
    setOpenRate(parseFloat(rate.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/email-open-rate-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Email Open Rate Calculator: Boost Email Performance | Free Tool</title>
        <meta name="description" content="Calculate your Email Open Rate instantly. Compare with industry benchmarks, get optimization tips, and improve your email marketing performance." />
        <meta name="keywords" content="email open rate calculator, email marketing metrics, open rate analytics, email performance, subject line optimization" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Email Open Rate Calculator: Optimize Your Campaigns" />
        <meta property="og:description" content="Calculate your Email Open Rate instantly. Get actionable insights to improve your email marketing performance and engagement." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Email Open Rate Calculator: Optimize Your Campaigns" />
        <meta property="twitter:description" content="Calculate your Email Open Rate instantly. Get actionable insights to improve your email marketing performance and engagement." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Email Open Rate Calculator",
            "description": "Free calculator to measure and optimize email open rates",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Email Open Rate",
              "Industry Benchmarks",
              "Subject Line Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Email Open Rate",
              "description": "The percentage of email recipients who open a specific email campaign, measuring email marketing effectiveness."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Email Marketers, Digital Marketers, Business Owners"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Email Open Rate",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter number of emails sent"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input number of emails opened"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your open rate and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Email Open Rate Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Emails Sent</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={emailsSent}
                  onChange={(e) => setEmailsSent(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="10000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Emails Opened</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Eye className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={emailsOpened}
                  onChange={(e) => setEmailsOpened(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="2000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {openRate}%
            <Percent className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Email Open Rate</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Email Open Rate is {openRate}%. This means that {openRate}% of the emails you sent were opened by recipients.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>E-commerce: 15-20%</li>
          <li>B2B Services: 20-25%</li>
          <li>Retail: 18-22%</li>
          <li>Healthcare: 21-25%</li>
          <li>Financial Services: 20-24%</li>
          <li>Education: 23-27%</li>
          <li>Nonprofits: 25-30%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Open Rate Optimization Tips</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Craft compelling subject lines</li>
          <li>Personalize your emails</li>
          <li>Segment your email list</li>
          <li>Optimize send times</li>
          <li>Use recognizable sender names</li>
          <li>Clean your email list regularly</li>
          <li>Test different subject lines</li>
          <li>Ensure mobile optimization</li>
          <li>Maintain consistent sending schedule</li>
          <li>Monitor spam complaints</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Subject Line Best Practices</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Keep subject lines under 50 characters</li>
          <li>Use personalization tokens</li>
          <li>Create urgency when appropriate</li>
          <li>Avoid spam trigger words</li>
          <li>A/B test different approaches</li>
          <li>Include relevant emojis strategically</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Factors</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Send time affects open rates by up to 25%</li>
          <li>Personalized subject lines increase opens by 26%</li>
          <li>Mobile-optimized emails see 30% higher open rates</li>
          <li>List segmentation improves opens by 14%</li>
          <li>Regular list cleaning boosts rates by 3-5%</li>
        </ul>
      </div>
    </div>
  );
};

export default EmailOpenRateCalculator;
